import React, { useState } from "react";
import { motion } from "framer-motion";
import { LEARN } from "../../theme";
import { COUNTING_ENGINE_THEME, countingFrameConfig, countingSpokenWord } from "../../../../data/countingLadderCatalog";
import { ConfettiBurst } from "../../feedback/Celebration";
import {
  CARD_FACE, CornerPill, CountingPanel, CountingPiece, CountingRoot, DRAG_BUTTON, PanelChip, POP_SPRING, SupplyShelf, hexAlpha, trayPieceSizeCss,
  useCompleteOnce, useCountingDrag, useMeasuredBox,
} from "../CountingPiece";

const cellId = (i) => `cell-${i}`;

/** Board geometry as fractions of a tile (kept in one place so the fit maths matches the styles). */
const BOARD = { pad: 0.12, gap: 0.08, between: 0.16, border: 3, maxCell: 1.05, minCell: 44 };

/**
 * The biggest cell (px) that lets every board sit inside the measured box,
 * five cells across and two down per board. Null before measurement.
 */
function fitCell(box, frames) {
  if (!box) return null;
  const { width, height, tile } = box;
  const pad = tile * BOARD.pad;
  const gap = tile * BOARD.gap;
  const chrome = pad * 2 + BOARD.border * 2;
  const byWidth = (width - chrome - gap * 4) / 5;
  const byHeight = (height - frames * (chrome + gap) - (frames - 1) * tile * BOARD.between) / (2 * frames);
  return Math.max(BOARD.minCell, Math.floor(Math.min(byWidth, byHeight, tile * BOARD.maxCell)));
}

/**
 * A real ten-frame: 2 x 5 rounded cells on an ink-lined board (two boards
 * for the twenty-frame). Beads fill by tap, or by dragging beads from the
 * tray onto an empty cell (tap a bead, then tap a cell, also works).
 */
const FrameFillGame = ({ activity, speak, sfx, onComplete, reduceMotion = false }) => {
  const { target, startingCount, totalSlots } = countingFrameConfig(activity);
  const drag = activity.interaction === "drag";
  const accent = COUNTING_ENGINE_THEME.frameFill.accent;
  const [filled, setFilled] = useState([]);
  const [selected, setSelected] = useState(null);
  const [burst, setBurst] = useState(0);
  const complete = useCompleteOnce(onComplete);
  const total = startingCount + filled.length;
  const done = total >= target;
  const nextEmpty = Array.from({ length: totalSlots }, (_, i) => i).find((i) => i >= startingCount && !filled.includes(i));
  const supplyCount = Math.max(1, target - startingCount + 2);
  const supply = Array.from({ length: supplyCount }, (_, i) => i);
  const [usedBeads, setUsedBeads] = useState([]);

  const fill = (cell, beadId = null) => {
    if (done || cell < startingCount || cell >= totalSlots || filled.includes(cell)) return false;
    const next = [...filled, cell];
    setFilled(next);
    setSelected(null);
    if (beadId != null) setUsedBeads((u) => [...u, beadId]);
    sfx?.("snap");
    speak?.(countingSpokenWord(startingCount + next.length), { interrupt: true });
    if (startingCount + next.length === target) {
      setTimeout(() => sfx?.("correct"), 140);
      setBurst((b) => b + 1);
      complete();
    }
    return true;
  };

  const { dragProps, registerTarget, hoverId, guardTap, GhostLayer } = useCountingDrag({
    reduceMotion,
    sfx,
    onDrop: ({ payload, targetId, targetData }) => (targetId ? fill(targetData.cell, payload) : false),
  });

  const ghost = () => <CountingPiece asset="bead" variant="bare" size="100%" />;
  const frames = Math.ceil(totalSlots / 10);
  // Beads bigger than a frame cell would look wrong dropping in, so the tray caps at 1.3 tiles.
  const traySize = trayPieceSizeCss(supplyCount, { maxScale: 1.3 });
  // The boards are sized to the space the panel gives them (a twenty-frame
  // on a laptop must not push the tray out of the arena); until measured,
  // a ten-frame is 5.8 tiles wide.
  const { ref: boardsRef, box } = useMeasuredBox();
  const cell = fitCell(box, frames);
  const boardWidth = cell ? `${cell * 5 + box.tile * BOARD.gap * 4 + box.tile * BOARD.pad * 2 + BOARD.border * 2}px` : "min(100%, calc(var(--tile) * 5.8))";

  const renderCell = (i) => {
    const has = i < startingCount || filled.includes(i);
    const hover = hoverId === cellId(i);
    const isNext = !done && i === nextEmpty;
    const empty = !has;
    const inviting = isNext && (!drag || selected != null);
    return (
      <button
        key={i}
        type="button"
        ref={drag && empty ? (el) => registerTarget(cellId(i), el, { cell: i }) : undefined}
        className="counting-btn counting-target"
        aria-label={`Space ${i + 1}${has ? ", filled" : ", empty"}`}
        disabled={has || done}
        onClick={guardTap(() => {
          if (has || done) return;
          if (drag) {
            if (selected == null) return;
            fill(i, selected);
            return;
          }
          fill(i);
        })}
        style={{
          aspectRatio: "1 / 1", minWidth: 0, minHeight: 0, borderRadius: "calc(var(--tile) * 0.16)", padding: "8%", boxSizing: "border-box", "--target-accent": accent,
          border: `2px solid ${hexAlpha(LEARN.color.ink, 0.28)}`,
          background: hover ? hexAlpha(accent, 0.45) : inviting ? hexAlpha(accent, 0.2) : has ? LEARN.color.white : `linear-gradient(180deg, ${hexAlpha(LEARN.color.ink, 0.06)}, ${hexAlpha(LEARN.color.ink, 0.015)})`,
          boxShadow: inviting && !has
            ? `inset 0 0 0 3px ${hexAlpha(accent, 0.7)}, inset 0 3px 6px ${hexAlpha(LEARN.color.ink, 0.08)}`
            : has ? `inset 0 1px 2px ${hexAlpha(LEARN.color.ink, 0.05)}` : `inset 0 4px 8px ${hexAlpha(LEARN.color.ink, 0.12)}, inset 0 -1px 0 rgba(255,255,255,0.9)`,
          transform: hover ? "scale(1.08)" : "scale(1)", transition: "background 140ms, transform 140ms, box-shadow 140ms",
          cursor: has || done ? "default" : "pointer", display: "grid", placeItems: "center", touchAction: "manipulation", position: "relative", zIndex: hover ? 1 : 0,
        }}
      >
        {has && (
          <motion.span initial={reduceMotion || i < startingCount ? false : { scale: 0.3, opacity: 0, y: -12 }} animate={{ scale: 1, opacity: 1, y: 0 }} transition={POP_SPRING} style={{ display: "inline-flex", width: "100%", height: "100%" }}>
            <CountingPiece asset="bead" variant="bare" size="100%" phase={i} reduceMotion={reduceMotion} />
          </motion.span>
        )}
      </button>
    );
  };

  return (
    <CountingRoot>
      <CountingPanel
        grow
        tint={accent}
        chip={<PanelChip lead="Make" value={target} accent={accent} />}
        corner={<CornerPill>{total} of {target}</CornerPill>}
      >
        <div
          ref={boardsRef}
          style={{ flex: "1 1 0%", minHeight: 0, width: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: `calc(var(--tile) * ${BOARD.between})` }}
        >
          {Array.from({ length: frames }, (_, f) => (
            <div
              key={f}
              aria-label={`Ten frame ${f + 1}`}
              style={{
                display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: `calc(var(--tile) * ${BOARD.gap})`, padding: `calc(var(--tile) * ${BOARD.pad})`,
                width: boardWidth, maxWidth: "100%", flexShrink: 0, boxSizing: "border-box", background: CARD_FACE, borderRadius: "calc(var(--tile) * 0.24)",
                border: `${BOARD.border}px solid ${LEARN.color.ink}`, boxShadow: `inset 0 2px 0 rgba(255,255,255,0.9), 0 calc(var(--tile) * 0.07) 0 ${hexAlpha(LEARN.color.ink, 0.55)}, ${LEARN.shadow.float}`,
              }}
            >
              {Array.from({ length: Math.min(10, totalSlots - f * 10) }, (_, k) => renderCell(f * 10 + k))}
            </div>
          ))}
        </div>
        {burst > 0 && <ConfettiBurst key={`burst-${burst}`} count={28} originY={40} reduceMotion={reduceMotion} />}
      </CountingPanel>

      {drag && (
        <SupplyShelf label="Bead shelf">
          {supply.map((id) => {
            const used = usedBeads.includes(id);
            const isSel = selected === id;
            const dp = dragProps(id, ghost);
            return (
              <motion.div
                key={id}
                initial={reduceMotion ? false : { scale: 0, opacity: 0 }}
                animate={{ scale: used ? 0.001 : 1, opacity: used ? 0 : 1, y: isSel ? -8 : 0 }}
                transition={reduceMotion ? { duration: 0 } : { ...POP_SPRING, delay: used ? 0 : Math.min(id * 0.05, 0.5) }}
                style={{ display: "inline-flex", width: used ? 0 : undefined, overflow: used ? "hidden" : "visible" }}
              >
                <button
                  type="button"
                  {...dp}
                  aria-label={used ? "Bead in the frame" : "Bead"}
                  aria-hidden={used ? "true" : undefined}
                  tabIndex={used ? -1 : undefined}
                  className="counting-btn"
                  disabled={done || used}
                  onClick={guardTap(() => {
                    if (done) return;
                    sfx?.("pop");
                    setSelected((s) => (s === id ? null : id));
                  })}
                  style={{ ...DRAG_BUTTON, ...dp.style, padding: 0, cursor: done ? "default" : "grab" }}
                >
                  <CountingPiece asset="bead" variant="bare" size={traySize} phase={id} floats={!isSel && !done} lifted={isSel} ring={isSel ? accent : undefined} reduceMotion={reduceMotion} style={{ borderRadius: "50%" }} />
                </button>
              </motion.div>
            );
          })}
        </SupplyShelf>
      )}
      <GhostLayer />
    </CountingRoot>
  );
};

export default FrameFillGame;
